import React, { useState, useMemo } from 'react';

import { PageHeader } from 'components/PageHeader';

import { Button, Divider, Spin, Typography, Card, Upload, message, notification, Alert } from 'antd';

const { Title, Paragraph } = Typography;

import { LoadingOutlined, NotificationOutlined } from '@ant-design/icons';

import FeatherIcon from 'feather-icons-react';
import { ALLOWED_FILES, allowed2dForConversion, allowed3dForConversion } from '@partsbadger/utils';

import FileStore from 'stores/FileStore';

import { ErrorRender } from '@partsbadger/library';

import { observer } from 'mobx-react';

import DisplayUploadFile from 'components/FileCreationRequest/DisplayUploadFile';

import { FeatureTourModal } from '@partsbadger/library';

import send_file_creation from 'assets/images/featureTour/fileCreation/send-file-creation.gif';

const FileCreationgRequest: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [uploadType, setUploadType] = useState('file');
    const [showFile, setShowFile] = useState(false);

    const handleUpload3DFile = async (file: File) => {
        setLoading(true);
        await FileStore.upload2DAnd3DFile(file, '3D')
            .then(res => {
                setLoading(false);
                setShowFile(true);
            })
            .catch(err => {
                notification.error({
                    message: 'Error',
                    description: <ErrorRender error={err} />,
                });
                setLoading(false);
            });
    };

    const handleUpload2DFile = async (file: File) => {
        setLoading(true);
        await FileStore.upload2DAnd3DFile(file, '2D')
            .then(res => {
                setLoading(false);
                setShowFile(true);
            })
            .catch(err => {
                notification.error({
                    message: 'Error',
                    description: <ErrorRender error={err} />,
                });
                setLoading(false);
            });
    };

    const tourModalDescriptions = [
        {
            key: 0,
            title: 'Welcome to the 2D and 3D File Creation Request tool!',
            description: (
                <div className="flex flex-col">
                    <span>
                        2D files provide the full story of your part and 3D models help you receive the most accurate,
                        highest quality part possible. We can support successful production of your parts by creating
                        either of these files.
                    </span>
                    <span className="mt-2">
                        Simply upload your file based on the listed acceptable file types. Upon submission, your Sales
                        Representative will quickly work with a Design Engineer on a quote to present to you prior to
                        creating the .step or PDF file. The format will be based on your upload.
                    </span>
                </div>
            ),

            img: send_file_creation,
            alt: '2D and 3D File Creation Request Tutorial',
        },
    ];

    return (
        <>
            <PageHeader>
                <div className="d-flex justify-content-between align-items-baseline mb-3">
                    <Title level={1}>2D and 3D File Creation Request</Title>
                </div>
            </PageHeader>
            <div className="container py-4">
                {showFile ? (
                    <DisplayUploadFile
                        removeUpload={() => {
                            setShowFile(false);
                        }}
                    />
                ) : (
                    <>
                        <FeatureTourModal
                            descriptions={tourModalDescriptions}
                            onClose={() => {
                                FileStore.setCloseTheFileCreationTour();
                            }}
                            isOpen={!FileStore.isFileCreationTourClosed}
                            modalWidth="1100px"
                        />

                        <Alert
                            message={<Title level={3}>New Features!</Title>}
                            description={
                                <>
                                    <a
                                        className="text-red-600 underline"
                                        onClick={() => {
                                            FileStore.setOpenTheFileCreationTour();
                                        }}
                                    >
                                        Click here
                                    </a>{' '}
                                    <span>
                                        to open up a Walkthrough which will guide you through all the new features on
                                        this page!
                                    </span>
                                </>
                            }
                            type="info"
                            closable
                            // onClose={onClose}
                            className="mb-4"
                            style={{ backgroundColor: '#FFF', borderColor: '#D6D6D6' }}
                            showIcon={true}
                            icon={<NotificationOutlined className="pt-1" />}
                        />

                        <Alert
                            type="info"
                            showIcon={true}
                            message="File Creation Costs"
                            description={
                                <div className="flex flex-col">
                                    <div>
                                        <span>
                                            Depending on part complexity, creation cost can start at $150 per file. This
                                            request will be sent to your Sales Representative via email with the file
                                            attached to manage your Submission.
                                        </span>
                                    </div>
                                    <div className="mt-4">
                                        <Title level={4}>2D/3D File Creation</Title>
                                        <span>
                                            We offer a service where you can request our engineers to create either a 2D
                                            rendition of a 3D file or a 3D rendition of a 2D file.
                                        </span>
                                    </div>
                                </div>
                            }
                            className="mt-3 mb-3 align-center"
                        />

                        <Card bordered={true} bodyStyle={{ padding: '1rem' }} className="first-step shadow-sm">
                            <Spin spinning={loading} tip={'Uploading...'} indicator={<LoadingOutlined />}>
                                <Upload.Dragger
                                    name="file"
                                    multiple={false}
                                    showUploadList={false}
                                    beforeUpload={file => {
                                        const type = file.name.split('.').pop();

                                        const isLt2M = file.size / 1024 / 1024 <= 100;

                                        if (!isLt2M) {
                                            message.error('File must be smaller than 100MB!', 10);
                                            return false;
                                        }

                                        const isAllowed3DFile = ALLOWED_FILES.allowed3d_for_conversion_tool.filter(
                                            file => {
                                                return file?.toLowerCase() === type?.toLowerCase();
                                            }
                                        );

                                        const isAllowed2DFile = ALLOWED_FILES.allowed2d_for_conversion_tool.filter(
                                            file => {
                                                return file?.toLowerCase() === type?.toLowerCase();
                                            }
                                        );

                                        if (isAllowed3DFile.length === 0 && isAllowed2DFile.length === 0) {
                                            message.error('The file type is not allowed, try again.', 3);
                                        }

                                        if (isAllowed3DFile.length === 1) {
                                            handleUpload3DFile(file);
                                        }

                                        if (isAllowed2DFile.length === 1) {
                                            handleUpload2DFile(file);
                                        }
                                    }}
                                >
                                    <div className="py-4">
                                        <p
                                            className="ant-upload-text pb-2"
                                            style={{ fontSize: '1.6rem', fontWeight: 600 }}
                                        >
                                            Select File to Upload
                                        </p>

                                        <p className="ant-upload-hint w-full m-auto">
                                            <span>Drag and drop your 2D or 3D file</span>
                                        </p>

                                        <div style={{ width: '6rem', margin: '0 auto' }}>
                                            <Divider>or</Divider>
                                        </div>

                                        <Button
                                            type={'primary'}
                                            className="d-flex flex-row align-items-center mx-auto mb-2 shadow-light"
                                            icon={<FeatherIcon icon="upload-cloud" size="22" className="mr-2" />}
                                            onClick={() => setUploadType('file')}
                                        >
                                            {uploadType === 'directory' ? 'Upload Folder' : 'Upload File'}
                                        </Button>

                                        <p className="flex flex-col pt-3">
                                            <span className="text-uppercase">
                                                <strong>2D Extensions:</strong>{' '}
                                                {allowed2dForConversion.replace('pdf', '').replace(/,/g, ' ')}
                                            </span>
                                            <span className="text-uppercase">
                                                <strong>3D Extensions:</strong>{' '}
                                                {allowed3dForConversion.replace('step', '').replace(/,/g, ' ')}
                                            </span>

                                            <span>100 MB Max File Size</span>
                                        </p>
                                    </div>
                                </Upload.Dragger>
                            </Spin>
                        </Card>
                    </>
                )}
            </div>
        </>
    );
};

export default observer(FileCreationgRequest);
